import {
  filter,
  find,
  flatMap,
  get,
  keyBy,
  map,
  reduce,
  sortBy,
  trim,
  toLower,
} from "lodash";

import { getGroupType } from "src/util/groupType";
import { reportError } from "src/services/errorReporting";
import { isSupplyGroup } from "src/util/customerConfig";

const UNKNOWN_NAME = "Unknown";

export const getName = (person) => {
  let name = "";
  if (get(person, "preferredName")) {
    name += person.preferredName;
  } else if (get(person, "firstName")) {
    name += person.firstName;
  }

  if (name.length > 0) {
    name += " ";
  }

  if (get(person, "surname")) {
    name += person.surname;
  }

  return trim(name) || UNKNOWN_NAME;
};

export const getInitials = (person) => {
  let initials = "";

  if (person.preferredName) {
    initials += person.preferredName[0];
  } else if (person.firstName) {
    initials += person.firstName[0];
  }

  if (person.surname) {
    initials += person.surname[0];
  }

  return initials || "?";
};

export const getPreferredFirstName = (person) => {
  return getName(person).split(" ")[0];
};

export const getDemandGroupIds = (userPerson, groupTypes) => {
  if (!userPerson || !groupTypes) {
    return [];
  }

  // find demand teams types from config
  const demandTypes = filter(groupTypes, "isDemand");
  const demandTypeNames = map(demandTypes, "name");
  // filter memberOf by team types
  const demandGroups = filter(userPerson.memberOf, (group) =>
    demandTypeNames.includes(group.type)
  );
  const demandGroupIds = map(demandGroups, "id");
  return demandGroupIds;
};

export const getPersonSupplyGroup = (person, groupTypes) =>
  find(person?.memberOf, (group) => isSupplyGroup(groupTypes, group));

export const sortMembersByName = (members) =>
  sortBy(members, (member) => toLower(getName(member)));

export const getUniqueWorkmates = (userPerson, groups) => {
  const demandTeamMates = flatMap(groups, "members");
  const teamMatesById = keyBy(demandTeamMates, "aggregateId");
  // remove the user from the results
  delete teamMatesById[userPerson.aggregateId];
  const uniqueWorkmates = Object.values(teamMatesById);
  return sortMembersByName(uniqueWorkmates);
};

export const getMapperAllocationsLink = (person) => {
  return `${process.env.REACT_APP_PEOPLE_MAPPER_URL}/people/${person.aggregateId}/allocations`;
};

export const getPersonEmailAttribute = (person) =>
  find(get(person, "attributes"), { attributeType: "id", subtype: "email" });

export const getPersonTelephoneAttribute = (person) =>
  find(get(person, "attributes"), { attributeType: "phone", subtype: "work" });

export const getPersonMobileNumberAttribute = (person) =>
  find(get(person, "attributes"), {
    attributeType: "phone",
    subtype: "mobile",
  });

const attributeText = (attribute) => {
  const value = attribute?.value;
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
};

/** Direct email, then the id/email attribute. Not a phone number. */
export const profileEmail = (person) => {
  const direct = person?.email;
  if (typeof direct === "string" && direct.trim().length > 0) {
    return direct.trim();
  }
  return attributeText(getPersonEmailAttribute(person));
};

/** Mobile subtype only. A work phone is not a mobile. */
export const profileMobile = (person) =>
  attributeText(getPersonMobileNumberAttribute(person));

/** Header contact phone: mobile when present, otherwise work. */
export const profileContactPhone = (person) =>
  profileMobile(person) || attributeText(getPersonTelephoneAttribute(person));

export const getEmailsFromPersonAttributes = (emailAttributes) =>
  reduce(
    emailAttributes,
    (result, { value }) => {
      if (value.attributes) {
        return [...result, get(getPersonEmailAttribute(value), "value")];
      }
      return result;
    },
    []
  );

export const onPersonEmailClick = (person) => (event) => {
  event.preventDefault();
  const emailAttribute = getPersonEmailAttribute(person);

  navigator.clipboard.writeText(emailAttribute?.value || "");
};

// libphonenumber-js is large, so it is not bundled with the app shell. Callers
// that show a phone button call preloadPhoneParser() so it is ready before the
// click; once loaded, the click handler runs synchronously and keeps the
// browser's user-gesture token (Safari blocks window.open after an await).
let phoneParser = null;
let phoneParserLoad = null;

// Seam for tests: jsdom cannot replace window.location.
export const phoneNavigation = {
  to: (uri) => window.location.assign(uri),
};

export const preloadPhoneParser = () => {
  if (!phoneParserLoad) {
    phoneParserLoad = import("libphonenumber-js").then((module) => {
      phoneParser = module.parsePhoneNumberFromString;
      return phoneParser;
    });
    phoneParserLoad.catch(() => {
      phoneParserLoad = null;
    });
  }
  return phoneParserLoad;
};

export const onPersonTelephoneClick = (person, onError) => (event) => {
  event.preventDefault();
  const telephoneAttribute = getPersonTelephoneAttribute(person);
  const mobileNumber = getPersonMobileNumberAttribute(person);

  if (!telephoneAttribute && !mobileNumber) {
    return undefined;
  }

  // prefer telephone over mobile
  // use lodash get, either of attributes could be null
  const phoneNumber =
    get(telephoneAttribute, "value") || get(mobileNumber, "value");

  const fail = (err) => {
    if (onError) {
      onError(err);
    }
    reportError(err);
  };

  const open = (parse, navigate) => {
    try {
      // parsePhoneNumber can throw error if invalid format
      navigate(parse(phoneNumber).getURI());
    } catch (err) {
      fail(err);
    }
  };

  if (phoneParser) {
    open(phoneParser, (uri) => window.open(uri));
    return undefined;
  }

  // Not loaded yet (clicked before the preload finished). The click gesture may
  // have expired by the time the parser arrives, so navigate to the tel: link
  // instead of opening a window, which browsers can block after an async gap.
  return preloadPhoneParser().then(
    (parse) => open(parse, (uri) => phoneNavigation.to(uri)),
    fail
  );
};

const findAllocatedFte = (member, team) =>
  get(
    find(
      get(member, "allocations"),
      (allocation) => get(allocation, "targetGroupId") === get(team, "id")
    ),
    "fte",
    null
  );

// only display fte if team is demand and fte is not 0 or equal to the member's fte.
export const findFteToDisplay = (member, team, groupTypes) => {
  const groupType = getGroupType(team, groupTypes);

  if (groupType?.isDemand) {
    const fte = findAllocatedFte(member, team);
    if (fte !== member.fte) {
      return fte;
    }
  }
  return null;
};

export const isValidPerson = (personDetails) => {
  if (!personDetails) {
    return false;
  }

  const personName = getName(personDetails);
  return personName !== UNKNOWN_NAME;
};

export const fullLegalName = ({ firstName, surname }) => {
  const first = firstName || "<Unknown>";
  const last = surname || "<Unknown>";
  return `${first} ${last}`.trim();
};

const countManagingDirectReports = (person) =>
  (person.managing ?? []).filter(
    (reportee) => reportee?.aggregateId !== person.aggregateId
  ).length;

/**
 * Direct-reports count from HRIS: backend `span`, else `managing` length.
 * @param {Object} person - The person object
 * @returns {number} - The count of direct reports (excluding self-reports)
 */
export const getDirectReportsCount = (person) => {
  if (!person) {
    return 0;
  }

  if (typeof person.span === "number") {
    return person.span;
  }

  return countManagingDirectReports(person);
};

/**
 * Checks if a person is a placeholder role
 * @param {Object} person - The person object to check
 * @returns {boolean} - True if the person is a placeholder
 */
export const isPlaceholderRole = (person) => {
  return person?.isPlaceholder === true;
};

/**
 * Checks if a person is a vacant role (placeholder or vacancy)
 * Used for list display purposes
 * @param {Object} person - The person object to check
 * @returns {boolean} - True if the person is a placeholder or vacancy
 */
export const isVacantRole = (person) => {
  return !!(person?.isPlaceholder || person?.isVacancy);
};
